import { logger } from '../../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { tripFeatureManager } from '../../../services/trip-feature-manager.js'

const log = logger.create('map')
import { calculateDestinationPoint } from '../geo-utils.js'
import { db } from '../../../database/schema.js'
import type { Feature } from '../../../schemas/feature.js'
import type { BaseShape } from '../shapes/base-shape.js'
import {
  computeHandlePositions as computeHandlePositionsPure,
  computeMoveFeature,
  computeMoveVertex,
  computeResizeCircle,
  computeResizeRectangleCorner,
  type FeatureGeometry,
  type FeatureLike,
  type LngLatTuple,
} from '../../../lib/edit-geometry-utils.js'

export interface EditHandleCallbacks {
  getShapeComponent: (featureId: string) => BaseShape | undefined
  refreshFeatureOnMap: (featureId: string) => Promise<void>
  updateContextMode: () => void
}

export class EditHandleManager {
  private map?: maplibregl.Map
  private editHandleMarkers: Map<string, maplibregl.Marker[]> = new Map()
  private isDraggingHandle = false
  private dragStartPoint?: maplibregl.LngLat | undefined
  private _boundHandleDrag?: ((e: MouseEvent) => void) | undefined
  private _boundEndDrag?: ((e: MouseEvent) => void) | undefined
  private _cachedDragFeature?: Feature | undefined
  private _dragOriginalFeature?: Feature | undefined

  constructor(private callbacks: EditHandleCallbacks) {}

  setMap(map: maplibregl.Map): void {
    this.map = map
  }

  async showEditHandlesForSelectedFeatures(): Promise<void> {
    const selectedFeatures = tripFeatureManager.getSelectedFeatures()
    for (const featureId of selectedFeatures) {
      await this.showEditHandles(featureId)
    }
  }

  async showEditHandles(featureId: string): Promise<void> {
    log.debug('🔧 Showing edit handles for feature:', featureId)
    this.hideEditHandles(featureId)

    const feature = await tripFeatureManager.getFeature(featureId)
    if (!feature || !this.map) {
      return
    }

    log.debug('🔧 Feature data for handles:', {
      type: feature.type,
      radius: feature.style?.radius ?? feature.properties?.radius,
    })

    const handleGroup: maplibregl.Marker[] = []
    this.editHandleMarkers.set(featureId, handleGroup)

    switch (feature.type) {
      case 'point':
        this.createPointHandles(feature, handleGroup)
        break
      case 'line':
        this.createLineHandles(feature, handleGroup)
        break
      case 'polygon':
        this.createPolygonHandles(feature, handleGroup)
        break
      case 'circle':
        this.createCircleHandles(feature, handleGroup)
        break
      case 'rectangle':
        this.createRectangleHandles(feature, handleGroup)
        break
    }

    log.debug(
      '🔧 ✅ Edit handles created for',
      feature.type,
      'feature with radius:',
      feature.style?.radius ?? feature.properties?.radius
    )
  }

  hideEditHandles(featureId: string): void {
    const handleGroup = this.editHandleMarkers.get(featureId)
    if (handleGroup) {
      handleGroup.forEach((m: maplibregl.Marker) => m.remove())
      this.editHandleMarkers.delete(featureId)
    }
  }

  hideAllEditHandles(): void {
    log.debug('🔧 Hiding all edit handles')
    log.debug('🔧 hideAllEditHandles called from:')
    this.editHandleMarkers.forEach(handleGroup => {
      handleGroup.forEach((m: maplibregl.Marker) => m.remove())
    })
    this.editHandleMarkers.clear()
    // activeHandleFeatureId cleared
  }

  updateHandlePositions(featureId: string, feature: FeatureLike): void {
    const existingHandleGroup = this.editHandleMarkers.get(featureId)
    if (!existingHandleGroup || existingHandleGroup.length === 0) {
      return
    }

    const positions = this.computeHandlePositions(feature)
    const count = Math.min(existingHandleGroup.length, positions.length)
    for (let i = 0; i < count; i++) {
      existingHandleGroup[i].setLngLat(positions[i])
    }
  }

  private computeHandlePositions(feature: FeatureLike): [number, number][] {
    return computeHandlePositionsPure(feature)
  }

  private createPointHandles(feature: Feature, handleGroup: maplibregl.Marker[]): void {
    const coords = (feature.geometry as GeoJSON.Point).coordinates
    const center: [number, number] = [coords[0], coords[1]]

    const dragEl = this.createHandleElement('drag')
    const dragMarker = new maplibregl.Marker({ element: dragEl, anchor: 'center' })
      .setLngLat(center)
      .addTo(this.map!)
    this.setupHandleDragging(dragMarker, feature.id, 'drag')
    handleGroup.push(dragMarker)
  }

  private createLineHandles(feature: Feature, handleGroup: maplibregl.Marker[]): void {
    const coords = (feature.geometry as GeoJSON.LineString).coordinates

    coords.forEach((coord: number[], index: number) => {
      const vertexEl = this.createHandleElement('vertex')
      const vertexMarker = new maplibregl.Marker({ element: vertexEl, anchor: 'center' })
        .setLngLat([coord[0], coord[1]])
        .addTo(this.map!)
      this.setupHandleDragging(vertexMarker, feature.id, 'vertex', index)
      handleGroup.push(vertexMarker)

      if (index < coords.length - 1) {
        const nextCoord = coords[index + 1]
        const midEl = this.createHandleElement('midpoint')
        const midpointMarker = new maplibregl.Marker({ element: midEl, anchor: 'center' })
          .setLngLat([(coord[0] + nextCoord[0]) / 2, (coord[1] + nextCoord[1]) / 2])
          .addTo(this.map!)
        this.setupHandleDragging(midpointMarker, feature.id, 'midpoint', index)
        handleGroup.push(midpointMarker)
      }
    })
  }

  private createPolygonHandles(feature: Feature, handleGroup: maplibregl.Marker[]): void {
    const coords = (feature.geometry as GeoJSON.Polygon).coordinates[0]
    const vertices = coords.slice(0, -1)

    vertices.forEach((coord: number[], index: number) => {
      const vertexEl = this.createHandleElement('vertex')
      const vertexMarker = new maplibregl.Marker({ element: vertexEl, anchor: 'center' })
        .setLngLat([coord[0], coord[1]])
        .addTo(this.map!)
      this.setupHandleDragging(vertexMarker, feature.id, 'vertex', index)
      handleGroup.push(vertexMarker)

      const nextIndex = (index + 1) % vertices.length
      const nextCoord = vertices[nextIndex]
      const midEl = this.createHandleElement('midpoint')
      const midpointMarker = new maplibregl.Marker({ element: midEl, anchor: 'center' })
        .setLngLat([(coord[0] + nextCoord[0]) / 2, (coord[1] + nextCoord[1]) / 2])
        .addTo(this.map!)
      this.setupHandleDragging(midpointMarker, feature.id, 'midpoint', index)
      handleGroup.push(midpointMarker)
    })
  }

  private createCircleHandles(feature: Feature, handleGroup: maplibregl.Marker[]): void {
    const coords = (feature.geometry as GeoJSON.Point).coordinates
    const center = new maplibregl.LngLat(coords[0], coords[1])
    const radius =
      feature.style?.radius ?? (feature.properties?.radius as number | undefined) ?? 100
    const bearing = 90
    const radiusPoint = calculateDestinationPoint(center, radius, bearing)

    const radiusEl = this.createHandleElement('radius')
    const radiusMarker = new maplibregl.Marker({ element: radiusEl, anchor: 'center' })
      .setLngLat(radiusPoint)
      .addTo(this.map!)
    this.setupHandleDragging(radiusMarker, feature.id, 'radius')
    handleGroup.push(radiusMarker)

    const dragEl = this.createHandleElement('drag')
    const dragMarker = new maplibregl.Marker({ element: dragEl, anchor: 'center' })
      .setLngLat(center)
      .addTo(this.map!)
    this.setupHandleDragging(dragMarker, feature.id, 'drag')
    handleGroup.push(dragMarker)
  }

  private createRectangleHandles(feature: Feature, handleGroup: maplibregl.Marker[]): void {
    const coords = (feature.geometry as GeoJSON.Polygon).coordinates[0]
    const corners = coords.slice(0, -1)

    corners.forEach((coord: number[], index: number) => {
      const cornerEl = this.createHandleElement('corner')
      const cornerMarker = new maplibregl.Marker({ element: cornerEl, anchor: 'center' })
        .setLngLat([coord[0], coord[1]])
        .addTo(this.map!)
      this.setupHandleDragging(cornerMarker, feature.id, 'corner', index)
      handleGroup.push(cornerMarker)
    })

    const centerLat =
      corners.reduce((sum: number, coord: number[]) => sum + coord[1], 0) / corners.length
    const centerLng =
      corners.reduce((sum: number, coord: number[]) => sum + coord[0], 0) / corners.length

    const dragEl = this.createHandleElement('drag')
    const dragMarker = new maplibregl.Marker({ element: dragEl, anchor: 'center' })
      .setLngLat([centerLng, centerLat])
      .addTo(this.map!)
    this.setupHandleDragging(dragMarker, feature.id, 'drag')
    handleGroup.push(dragMarker)
  }

  private createHandleElement(
    type: 'vertex' | 'midpoint' | 'drag' | 'corner' | 'radius'
  ): HTMLDivElement {
    const el = document.createElement('div')
    el.style.cursor = 'grab'
    el.style.zIndex = '2000'

    switch (type) {
      case 'vertex':
        el.style.width = '10px'
        el.style.height = '10px'
        el.style.background = '#ffffff'
        el.style.border = '1px solid #000000'
        el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.5)'
        break
      case 'midpoint':
        el.style.width = '6px'
        el.style.height = '6px'
        el.style.background = '#ffffff'
        el.style.border = '1px solid #000000'
        el.style.opacity = '0.8'
        el.style.boxShadow = '0 1px 3px rgba(0,0,0,0.5)'
        break
      case 'drag':
        el.style.width = '12px'
        el.style.height = '12px'
        el.style.background = '#3f5a7a'
        el.style.border = '2px solid #3f5a7a'
        el.style.borderRadius = '50%'
        el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.5)'
        break
      case 'corner':
        el.style.width = '10px'
        el.style.height = '10px'
        el.style.background = '#ffffff'
        el.style.border = '1px solid #000000'
        el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.5)'
        break
      case 'radius':
        el.style.width = '10px'
        el.style.height = '10px'
        el.style.background = '#ffffff'
        el.style.border = '2px solid #3f5a7a'
        el.style.borderRadius = '50%'
        el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.5)'
        break
    }
    return el
  }

  private setupHandleDragging(
    marker: maplibregl.Marker,
    featureId: string,
    handleType: string,
    index?: number
  ): void {
    marker.getElement().addEventListener('mousedown', e => {
      e.stopPropagation()
      e.preventDefault()
      void this.startHandleDrag(featureId, handleType, index, marker)
    })
  }

  private async startHandleDrag(
    featureId: string,
    handleType: string,
    index: number | undefined,
    marker: maplibregl.Marker
  ): Promise<void> {
    this.isDraggingHandle = true
    // activeHandleFeatureId = featureId
    this.dragStartPoint = marker.getLngLat()

    const feature = await tripFeatureManager.getFeature(featureId)
    if (!feature) {
      return
    }
    this._cachedDragFeature = JSON.parse(JSON.stringify(feature))
    this._dragOriginalFeature = JSON.parse(JSON.stringify(feature))

    const handleGroup = this.editHandleMarkers.get(featureId)
    if (handleGroup) {
      handleGroup.forEach(m => (m.getElement().style.display = 'none'))
    }

    this._boundHandleDrag = (e: MouseEvent) => {
      if (!this.isDraggingHandle || !this.map || !this._cachedDragFeature) {
        return
      }
      e.stopPropagation()
      e.preventDefault()
      const rect = this.map.getCanvas().getBoundingClientRect()
      const lngLat = this.map.unproject(
        new maplibregl.Point(e.clientX - rect.left, e.clientY - rect.top)
      )
      this.updateFeatureGeometryPreview(featureId, handleType, index, lngLat)
    }

    this._boundEndDrag = (e: MouseEvent) => {
      if (!this.isDraggingHandle || !this.map) {
        return
      }
      this.isDraggingHandle = false
      // activeHandleFeatureId cleared
      this._cachedDragFeature = undefined
      this._dragOriginalFeature = undefined
      document.removeEventListener('mousemove', this._boundHandleDrag!)
      document.removeEventListener('mouseup', this._boundEndDrag!)
      this._boundHandleDrag = undefined
      this._boundEndDrag = undefined
      const rect = this.map.getCanvas().getBoundingClientRect()
      const lngLat = this.map.unproject(
        new maplibregl.Point(e.clientX - rect.left, e.clientY - rect.top)
      )
      void this.applyHandleChange(featureId, handleType, index, lngLat)
        .then(() => new Promise(resolve => setTimeout(resolve, 150)))
        .then(() => this.showEditHandles(featureId))
        .then(() => {
          tripFeatureManager.selectFeature(featureId)
          this.callbacks.updateContextMode()
        })
    }

    document.addEventListener('mousemove', this._boundHandleDrag)
    document.addEventListener('mouseup', this._boundEndDrag)
  }

  private async applyHandleChange(
    featureId: string,
    handleType: string,
    index: number | undefined,
    newPosition: maplibregl.LngLat
  ): Promise<void> {
    log.debug(`🔧 Applying ${handleType} change for feature ${featureId}:`, newPosition)

    const feature = await tripFeatureManager.getFeature(featureId)
    if (!feature) {
      return
    }

    switch (handleType) {
      case 'drag':
        await this.moveFeature(featureId, newPosition)
        break
      case 'vertex':
        await this.moveVertex(featureId, index!, newPosition)
        break
      case 'midpoint':
        await this.insertVertex(featureId, index!, newPosition)
        break
      case 'radius':
        await this.resizeCircle(featureId, newPosition)
        break
      case 'corner':
        await this.resizeRectangleCorner(featureId, index!, newPosition)
        break
    }
  }

  private async moveFeature(featureId: string, newPosition: maplibregl.LngLat): Promise<void> {
    log.debug('🔧 Moving entire feature to:', newPosition)
    await tripFeatureManager.moveFeature(featureId, { lat: newPosition.lat, lng: newPosition.lng })
  }

  private async moveVertex(
    _featureId: string,
    vertexIndex: number,
    newPosition: maplibregl.LngLat
  ): Promise<void> {
    log.debug('🔧 Moving vertex', vertexIndex, 'to:', newPosition)
    // TODO: Implement vertex movement in trip feature manager
  }

  private async insertVertex(
    _featureId: string,
    afterIndex: number,
    newPosition: maplibregl.LngLat
  ): Promise<void> {
    log.debug('🔧 Inserting vertex after', afterIndex, 'at:', newPosition)
    // TODO: Implement vertex insertion in trip feature manager
  }

  private async resizeCircle(featureId: string, radiusPosition: maplibregl.LngLat): Promise<void> {
    log.debug('🔧 Resizing circle radius to:', radiusPosition)

    const feature = await tripFeatureManager.getFeature(featureId)
    if (!feature) {
      return
    }

    const c = (feature.geometry as GeoJSON.Point).coordinates
    const newRadius = computeResizeCircle(c[0], c[1], radiusPosition.lng, radiusPosition.lat)

    log.debug('🔧 New radius calculated:', newRadius, 'meters')

    await Promise.all([
      tripFeatureManager.applyFeatureStyle(featureId, {
        ...feature.style,
        radius: newRadius,
      }),
      db.features.update(featureId, {
        properties: {
          ...feature.properties,
          radius: newRadius,
        },
        updatedAt: new Date().toISOString(),
      }),
    ])

    log.debug('🔧 ✅ Circle radius updated in database')
    await this.callbacks.refreshFeatureOnMap(featureId)
    log.debug('🔧 ✅ Circle feature refreshed on map')
  }

  private async resizeRectangleCorner(
    featureId: string,
    cornerIndex: number,
    newPosition: maplibregl.LngLat
  ): Promise<void> {
    log.debug('🔧 Resizing rectangle corner', cornerIndex, 'to:', newPosition)

    const feature = await tripFeatureManager.getFeature(featureId)
    if (feature?.type !== 'rectangle') {
      return
    }

    const corners = (feature.geometry as GeoJSON.Polygon).coordinates[0].slice(0, -1)
    const closedCorners = computeResizeRectangleCorner(corners, cornerIndex, [
      newPosition.lng,
      newPosition.lat,
    ])
    const newGeometry: GeoJSON.Polygon = {
      type: 'Polygon',
      coordinates: [closedCorners],
    }

    await db.features.update(featureId, {
      geometry: newGeometry,
      updatedAt: new Date().toISOString(),
    })

    log.debug('🔧 ✅ Rectangle geometry updated in database')
    await this.callbacks.refreshFeatureOnMap(featureId)
    log.debug('🔧 ✅ Rectangle feature refreshed on map')
  }

  private updateFeatureGeometryPreview(
    _featureId: string,
    handleType: string,
    index: number | undefined,
    newPosition: maplibregl.LngLat
  ): void {
    const feature = this._cachedDragFeature
    if (!feature) {
      return
    }

    try {
      let updatedGeometry: FeatureGeometry | null = null

      switch (handleType) {
        case 'drag':
          updatedGeometry = this.previewMoveFeature(
            this._dragOriginalFeature ?? feature,
            newPosition
          )
          break
        case 'vertex':
          updatedGeometry = this.previewMoveVertex(feature, index!, newPosition)
          break
        case 'radius':
          updatedGeometry = this.previewResizeCircle(feature, newPosition)
          break
        case 'corner':
          updatedGeometry = this.previewResizeRectangleCorner(feature, index!, newPosition)
          break
      }

      if (updatedGeometry) {
        this._cachedDragFeature = { ...feature, geometry: updatedGeometry as GeoJSON.Geometry }
      }
    } catch (error) {
      log.error('🔧 ❌ Error updating geometry preview:', error)
    }
  }

  private previewMoveFeature(feature: Feature, newPosition: maplibregl.LngLat): FeatureGeometry {
    const shapeComponent = this.callbacks.getShapeComponent(feature.id)
    const dragStart: LngLatTuple | undefined = this.dragStartPoint
      ? [this.dragStartPoint.lng, this.dragStartPoint.lat]
      : undefined
    const geometry = computeMoveFeature(
      feature as unknown as FeatureLike,
      [newPosition.lng, newPosition.lat],
      dragStart
    )
    if (shapeComponent) {
      shapeComponent.feature = { ...feature, geometry: geometry as GeoJSON.Geometry }
      shapeComponent.updateSource()
    }
    return geometry
  }

  private previewMoveVertex(
    feature: Feature,
    vertexIndex: number,
    newPosition: maplibregl.LngLat
  ): FeatureGeometry {
    const shapeComponent = this.callbacks.getShapeComponent(feature.id)
    const geometry = computeMoveVertex(feature as unknown as FeatureLike, vertexIndex, [
      newPosition.lng,
      newPosition.lat,
    ])
    if (shapeComponent) {
      shapeComponent.feature = { ...feature, geometry: geometry as GeoJSON.Geometry }
      shapeComponent.updateSource()
    }
    return geometry
  }

  private previewResizeCircle(
    feature: Feature,
    radiusPosition: maplibregl.LngLat
  ): FeatureGeometry {
    const shapeComponent = this.callbacks.getShapeComponent(feature.id)
    if (feature.type === 'circle') {
      const c = (feature.geometry as GeoJSON.Point).coordinates
      const newRadius = computeResizeCircle(c[0], c[1], radiusPosition.lng, radiusPosition.lat)
      const updatedFeature = {
        ...feature,
        style: { ...(feature.style ?? {}), radius: newRadius },
        properties: { ...(feature.properties ?? {}), radius: newRadius },
      }
      if (shapeComponent) {
        shapeComponent.feature = updatedFeature
        shapeComponent.updateSource()
      }
      this._cachedDragFeature = updatedFeature
      return { type: 'Point', coordinates: (feature.geometry as GeoJSON.Point).coordinates }
    }
    return feature.geometry as unknown as FeatureGeometry
  }

  private previewResizeRectangleCorner(
    feature: Feature,
    cornerIndex: number,
    newPosition: maplibregl.LngLat
  ): FeatureGeometry {
    const shapeComponent = this.callbacks.getShapeComponent(feature.id)
    if (feature.type === 'rectangle') {
      const corners = (feature.geometry as GeoJSON.Polygon).coordinates[0].slice(0, -1)
      const closedCorners = computeResizeRectangleCorner(corners, cornerIndex, [
        newPosition.lng,
        newPosition.lat,
      ])
      const geometry: FeatureGeometry = { type: 'Polygon', coordinates: [closedCorners] }
      if (shapeComponent) {
        shapeComponent.feature = { ...feature, geometry: geometry as GeoJSON.Geometry }
        shapeComponent.updateSource()
      }
      return geometry
    }
    return feature.geometry as unknown as FeatureGeometry
  }

  dispose(): void {
    if (this._boundHandleDrag) {
      document.removeEventListener('mousemove', this._boundHandleDrag)
    }
    if (this._boundEndDrag) {
      document.removeEventListener('mouseup', this._boundEndDrag)
    }
    this.hideAllEditHandles()
  }
}
